/**
 * Display masks for sensitive values (ported from d2d-DLP; ZTA design evaluation D4, F13, decision D4 = A).
 *
 * The console showed what a policy blocked — the matched value, the full URL,
 * user emails, source IPs, device ids — raw, and MonoCell copied it whole.
 * Every such value is now drawn through one of these masks by default
 * (components/ds-dlp/MaskedValue.tsx), and a user who may reveal it opts in
 * per value.
 *
 * DISPLAY-LEVEL ONLY. The API still returns raw values, so they are in the
 * network response and in React state; this keeps them off the screen, out of
 * screenshots and out of the clipboard, not out of the browser. Masking at
 * the source needs a server change.
 *
 * Every function is pure, total (any string in, a string out) and never
 * returns more of the value than its rule allows — when a value doesn't parse
 * as its kind, it falls back to maskSecret rather than to the raw value.
 */

export const MASK = '•';
const DOTS = MASK.repeat(3);

export type MaskKind = 'email' | 'ip' | 'url' | 'secret' | 'matched' | 'id' | 'plain';

/** j•••@acme.com — the first character of the local part and the domain. */
export function maskEmail(value: string): string {
  const v = value.trim();
  const at = v.lastIndexOf('@');
  if (at <= 0 || at === v.length - 1) return maskSecret(v);
  return `${v[0]}${DOTS}@${v.slice(at + 1)}`;
}

const IPV4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/** 10.0.•.• for IPv4; the network half of an IPv6 address, tail masked. */
export function maskIp(value: string): string {
  const v = value.trim();
  const m = IPV4.exec(v);
  if (m) return `${m[1]}.${m[2]}.${MASK}.${MASK}`;
  if (v.includes(':') && /^[0-9a-f:.]+$/i.test(v)) {
    // Keep at most the first two groups (the provider block), never the
    // interface id: 2001:db8:•:•.
    const groups = v.split(':').filter(Boolean);
    const head = groups.slice(0, Math.min(2, Math.max(0, groups.length - 1)));
    return head.length ? `${head.join(':')}:${MASK}:${MASK}` : `${MASK}:${MASK}`;
  }
  return maskSecret(v);
}

/**
 * Scheme, host and path stay readable; every query value, the fragment and
 * any user:password are masked: https://x.com/a?q=•••&id=•••. A value with no
 * scheme ("x.com/a?q=1") keeps its own shape. A bare host has nothing to
 * mask and comes back unchanged.
 */
export function maskUrl(value: string): string {
  const v = value.trim();
  if (!v) return v;
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(v);
  let u: URL;
  try {
    u = new URL(hasScheme ? v : `http://${v}`);
  } catch {
    return maskSecret(v);
  }
  if (!u.hostname) return maskSecret(v);
  const auth = u.username || u.password ? `${DOTS}@` : '';
  const query = [...u.searchParams.keys()].map((k) => `${k}=${DOTS}`).join('&');
  const path = u.pathname === '/' && !v.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').includes('/') ? '' : u.pathname;
  return `${hasScheme ? `${u.protocol}//` : ''}${auth}${u.host}${path}${query ? `?${query}` : ''}${u.hash ? `#${DOTS}` : ''}`;
}

/**
 * A secret or a matched string: its length, and the first and last character
 * only when it is long enough that two characters don't give it away.
 * "4111111111111111" → "4••••••1 (16 chars)".
 */
export function maskSecret(value: string): string {
  const v = value ?? '';
  const n = [...v].length;
  if (n === 0) return '';
  const len = `(${n} ${n === 1 ? 'char' : 'chars'})`;
  if (n < 8) return `${MASK.repeat(6)} ${len}`;
  const chars = [...v];
  return `${chars[0]}${MASK.repeat(6)}${chars[n - 1]} ${len}`;
}

/** abcd…wxyz — an id keeps enough to tell two apart, not to reuse. */
export function maskId(value: string): string {
  const v = value.trim();
  if (v.length <= 10) return v.length <= 4 ? MASK.repeat(4) : `${v.slice(0, 2)}${DOTS}`;
  return `${v.slice(0, 4)}…${v.slice(-4)}`;
}

/**
 * Matched content: whatever a rule matched, in the mask its shape calls for —
 * an email stays recognisable as an email, a URL keeps its host — and
 * anything else as a secret.
 */
export function maskMatched(value: string): string {
  const v = value.trim();
  if (!v) return v;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return maskEmail(v);
  if (IPV4.test(v) || (/^[0-9a-f:]+$/i.test(v) && v.split(':').length > 2)) return maskIp(v);
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) return maskUrl(v);
  return maskSecret(v);
}

export function maskValue(kind: MaskKind, value: string): string {
  switch (kind) {
    case 'email': return maskEmail(value);
    case 'ip': return maskIp(value);
    case 'url': return maskUrl(value);
    case 'secret': return maskSecret(value);
    case 'matched': return maskMatched(value);
    case 'id': return maskId(value);
    case 'plain': return value;
  }
}

/** Shorten from the middle so both ends stay readable: "WKS-FIN…-0042". */
export function truncateMiddle(value: string, max: number): string {
  if (max < 5 || value.length <= max) return value;
  const keep = max - 1;
  const head = Math.ceil(keep / 2);
  return `${value.slice(0, head)}…${value.slice(value.length - (keep - head))}`;
}
